import { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  UploadCloud, FileSpreadsheet, Loader2, CheckCircle2, AlertCircle, Download, Database, ClipboardList,
} from "lucide-react";
import { toast } from "sonner";
import { api, downloadFile, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

function Dropzone({ testid, inputTestid, busy, isError, accept = ".xlsx,.xls", onFile, hint, idle }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  return (
    <div
      data-testid={testid}
      onClick={() => !busy && inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => { e.preventDefault(); setDragging(false); onFile(e.dataTransfer.files?.[0]); }}
      className={`cursor-pointer rounded-sm border-2 border-dashed p-8 flex flex-col items-center justify-center text-center transition-colors
        ${isError ? "border-[#F87171] bg-[#FEF2F2]" : dragging ? "border-[#2563EB] bg-blue-50" : "border-slate-300 bg-white hover:border-[#2563EB] hover:bg-slate-50"}`}
    >
      <input ref={inputRef} type="file" accept={accept} hidden
        data-testid={inputTestid} onChange={(e) => onFile(e.target.files?.[0])} />
      {busy ? (
        <><Loader2 className="animate-spin text-[#2563EB]" size={26} />
          <p className="mt-2 text-sm text-slate-600">Procesando…</p></>
      ) : (
        <>{idle}
          <p className="mt-2 text-sm font-medium text-slate-700">Arrastra el archivo aquí o haz clic para seleccionar</p>
          <p className="text-xs text-slate-400 mt-1 font-mono">{hint}</p></>
      )}
    </div>
  );
}

export function UploadPanel({ onCompleted, onViewResults }) {
  const qc = useQueryClient();
  const [reportBusy, setReportBusy] = useState(false);
  const [viewBusy, setViewBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [sampleBusy, setSampleBusy] = useState(false);

  const { data: cv } = useQuery({
    queryKey: ["current-view"],
    queryFn: async () => (await api.get("/recon/current-view")).data,
  });
  const meta = cv?.meta;

  const handleView = async (file) => {
    if (!file) return;
    if (!/\.(xlsx|xls)$/i.test(file.name)) { toast.error("El archivo debe ser .xlsx o .xls"); return; }
    setViewBusy(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/recon/current-view/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data.ok) {
        toast.success(`Vista actual cargada · ${data.meta?.rows ?? 0} líneas`);
        qc.invalidateQueries({ queryKey: ["current-view"] });
      } else {
        toast.error(formatApiError(data.errors?.join(" ")) || "No se pudo procesar la vista actual");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error al subir la vista actual");
    } finally {
      setViewBusy(false);
    }
  };

  const handleReport = async (file) => {
    if (!file) return;
    if (!/\.(xlsx|xls)$/i.test(file.name)) { toast.error("El archivo debe ser .xlsx o .xls"); return; }
    setReportBusy(true);
    setResult(null);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/recon/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult({ ...data, file_name: file.name });
      if (data.status === "completed") {
        toast.success(`Conciliación lista · ${data.summary?.total_imported ?? 0} líneas importadas`);
        onCompleted?.(data.run_id);
      } else {
        toast.error("El archivo tiene columnas obligatorias faltantes");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error al subir el archivo");
    } finally {
      setReportBusy(false);
    }
  };

  const downloadSample = async () => {
    setSampleBusy(true);
    try {
      await downloadFile("/recon/sample-file", "STORIS_sample.xlsx");
    } catch {
      toast.error("No se pudo descargar el ejemplo");
    } finally {
      setSampleBusy(false);
    }
  };

  const isError = result && result.status === "validation_error";

  return (
    <div className="max-w-4xl space-y-6" data-testid="upload-panel">
      {/* STEP 1 — current view (TE325) */}
      <section>
        <div className="flex items-center gap-2 mb-1">
          <span className="h-6 w-6 rounded-sm bg-[#0F172A] text-white text-xs font-bold flex items-center justify-center">1</span>
          <h2 className="font-heading text-lg font-bold tracking-tight text-slate-900">Vista actual de órdenes (TE325)</h2>
        </div>
        <p className="text-sm text-slate-500 mb-3">
          Es la <span className="font-medium text-slate-700">base contra la que se concilia</span>. Súbela una vez (o cuando cambie) — reemplaza las órdenes abiertas a nivel <span className="font-mono text-slate-700">Orden + Modelo</span>.
        </p>

        {meta && (
          <div className="mb-3 rounded-sm border border-[#34D399] bg-[#ECFDF5] px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap" data-testid="current-view-status">
            <span className="flex items-center gap-2 text-sm text-[#065F46]">
              <Database size={15} /> <span className="font-mono">{meta.file_name}</span>
            </span>
            <span className="text-xs text-[#065F46] font-mono">
              {meta.rows} líneas · {meta.distinct_orders} órdenes · {meta.distinct_stores} tiendas
            </span>
          </div>
        )}

        <Dropzone testid="current-view-dropzone" inputTestid="current-view-input"
          busy={viewBusy} hint=".xlsx · .xls" onFile={handleView}
          idle={<div className="h-11 w-11 rounded-sm bg-[#2563EB] text-white flex items-center justify-center"><Database size={22} /></div>} />
      </section>

      {/* STEP 2 — STORIS report (TE331) */}
      <section>
        <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
          <div className="flex items-center gap-2">
            <span className="h-6 w-6 rounded-sm bg-[#0F172A] text-white text-xs font-bold flex items-center justify-center">2</span>
            <h2 className="font-heading text-lg font-bold tracking-tight text-slate-900">Reporte Logistical Scheduling (TE331)</h2>
          </div>
          <Button variant="outline" onClick={downloadSample} disabled={sampleBusy}
            data-testid="download-sample-button" className="rounded-sm border-slate-300 h-9 gap-2">
            {sampleBusy ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
            Archivo de ejemplo
          </Button>
        </div>
        <p className="text-sm text-slate-500 mb-3">
          El export de STORIS que quieres conciliar contra la vista actual.
        </p>

        {!meta && (
          <div className="mb-3 rounded-sm border border-[#FBBF24] bg-[#FFFBEB] px-4 py-2.5 text-sm text-[#92400E] flex items-center gap-2" data-testid="no-current-view-warning">
            <AlertCircle size={15} /> Sube primero la vista actual (paso 1) para tener contra qué conciliar.
          </div>
        )}

        <Dropzone testid="upload-excel-dropzone" inputTestid="upload-file-input"
          busy={reportBusy} isError={isError} hint=".xlsx · .xls" onFile={handleReport}
          idle={<div className="h-11 w-11 rounded-sm bg-[#0F172A] text-white flex items-center justify-center"><UploadCloud size={22} /></div>} />

        {/* Validation error */}
        {isError && (
          <div className="mt-4 rounded-sm border border-[#F87171] bg-white overflow-hidden" data-testid="upload-validation-error">
            <div className="flex items-center gap-2 px-4 py-2.5 bg-[#FEE2E2] text-[#991B1B] text-sm font-semibold">
              <AlertCircle size={16} /> No se pudo procesar el archivo
            </div>
            <div className="p-4 space-y-3">
              {result.errors?.length > 0 && (
                <div className="bg-slate-900 text-slate-100 rounded-sm p-3 font-mono text-xs space-y-1 thin-scroll overflow-auto">
                  {result.errors.map((e, i) => <div key={i}>› {e}</div>)}
                </div>
              )}
              {result.missing_columns?.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">Columnas faltantes</p>
                  <div className="flex flex-wrap gap-1.5">
                    {result.missing_columns.map((c) => (
                      <span key={c} className="font-mono text-[11px] bg-[#FEE2E2] text-[#991B1B] border border-[#F87171] rounded-sm px-2 py-0.5">{c}</span>
                    ))}
                  </div>
                </div>
              )}
              {result.detected_columns?.length > 0 && (
                <p className="text-xs text-slate-400">Detectadas: <span className="font-mono">{result.detected_columns.join(", ")}</span></p>
              )}
            </div>
          </div>
        )}

        {/* Success */}
        {result && result.status === "completed" && (
          <div className="mt-4 rounded-sm border border-[#34D399] bg-white overflow-hidden" data-testid="upload-success">
            <div className="flex items-center justify-between gap-2 px-4 py-2.5 bg-[#D1FAE5] text-[#065F46] text-sm font-semibold">
              <span className="flex items-center gap-2"><CheckCircle2 size={16} /> Conciliación completada · {result.file_name}</span>
              <Button size="sm" data-testid="view-results-button" onClick={onViewResults}
                className="h-7 rounded-sm bg-[#065F46] hover:bg-[#065F46]/90 text-white text-xs">
                Ver resultados →
              </Button>
            </div>
            <div className="p-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
              {[
                ["Importados", result.summary?.total_imported, "#0F172A"],
                ["Conciliados", result.summary?.conciliado, "#059669"],
                ["Diferencias", result.summary?.diferencia, "#DC2626"],
                ["Pendientes", result.summary?.pendiente_storis, "#D97706"],
              ].map(([l, v, c]) => (
                <div key={l} className="border border-[#E2E8F0] rounded-sm p-3">
                  <div className="font-mono text-2xl font-light" style={{ color: c }}>{v ?? 0}</div>
                  <div className="text-[11px] uppercase tracking-wide text-slate-500 mt-1">{l}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Reference */}
      <div className="rounded-sm border border-[#E2E8F0] bg-white p-4">
        <div className="flex items-center gap-2 text-slate-700 mb-3">
          <ClipboardList size={16} />
          <span className="text-xs uppercase tracking-[0.05em] font-semibold">Cómo se cruzan los archivos</span>
        </div>
        <p className="text-sm text-slate-500">
          Cada línea del <span className="font-mono text-slate-700">Reporte (TE331)</span> se cruza contra la
          <span className="font-mono text-slate-700"> Vista actual (TE325)</span> por
          <span className="font-semibold text-slate-700"> Order Number + Model Number</span>, comparando fecha de entrega,
          estatus y cantidad. Lo que no cruza se marca como <span className="font-mono">Pendiente en STORIS</span> (en el reporte, no en la vista) o
          <span className="font-mono"> Solo en vista</span> (en la vista, no en el reporte).
        </p>
        <div className="flex items-center gap-2 mt-3 text-xs text-slate-500">
          <FileSpreadsheet size={14} />
          <span className="font-mono">Columnas clave: Order Number · Model Number · Dlvy/Est Date · Dlvy Stat · Selling/Ship From Location</span>
        </div>
      </div>
    </div>
  );
}
